/**
 * SourceCardActions - Action row for the source hover popover.
 *
 * Provides view (open in new tab), save (bookmark), and feedback (thumbs up/down)
 * actions at the bottom of the SourceCard hover popover.
 */

import { Button, type ButtonSize } from '@heidi/design-system/button';
import { ThumbsDownFilled, ThumbsUpFilled } from '@heidi/design-system/feedback-thumbs';
import { toast } from '@heidi/design-system/toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { useTranslations } from '@heidi/runtime/i18n';
import { cn, getDomainFaviconUrl, useIsMobile } from '@heidi/utils';
import { useQueryClient } from '@tanstack/react-query';
import {
  Bookmark,
  BookmarkCheck,
  ExternalLink,
  Eye,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import type {
  EnrichedReference,
  EvidenceSourceSurface,
  SimpleCitation,
} from '../../contracts';
import {
  CPD_HOMEPAGE_KEY,
  CPD_SAVED_SOURCES_KEY,
  CPD_SOURCES_INFINITE_KEY,
  buildViewerSource,
  getDomainFromUrl,
} from '../../contracts';
import { isFileCitation, isPartnerCitation } from '../../contracts/citationGuards';
import { saveSourceByUrl, unsaveSourceById, useCPDSavedSources } from '../../data';
import { FeedbackPopover } from './FeedbackPopover';
import { useEvidenceAnswerPorts } from './ports';
import { shouldOpenSourceInViewer } from './sourceViewerRouting';
import { useReferenceFeedback } from './useReferenceFeedback';

interface SourceCardActionsProps {
  source: SimpleCitation;
  /** Index of this source within the citation group (for feedback tracking) */
  index: number;
  /** Chat ID for feedback tracking */
  chatId?: string;
  /** Session ID for feedback tracking */
  sessionId?: string;
  /** Message ID for feedback tracking */
  messageId?: string;
  className?: string;
  /** Button size variant. Defaults to "xs" (hover card). The reference list passes "xxs" so the action row sits proportionally under the title. */
  size?: ButtonSize;
  /**
   * When true, removes the hover/active background fill from action buttons.
   * Only the icon colour change and tooltip convey hover state.
   * The reference list passes `noHoverBg={compact}` so the in-session sidebar
   * skips the fill (too tight, causes visual misalignment) while the
   * out-of-session full-screen view keeps it.
   */
  noHoverBg?: boolean;
  /** Which side the feedback popover opens on. @default "top" */
  feedbackPopoverSide?: 'top' | 'right' | 'bottom' | 'left';
  /** Show a bookmark/save-for-later action for this source (EVI-1204). */
  showSaveAction?: boolean;
  /** Layout variant. "v3" renders all buttons in a compact inline group (parent handles positioning). */
  variant?: 'v2' | 'v3';
  /**
   * EVI-1486: surface name for `evidence_source_clicked` analytics fired by
   * the eye / partner-CTA buttons in this row. Defaults to
   * `inline_citation_badge` since this row most commonly sits inside a
   * citation popover; override to `references_section` from ReferenceCard.
   */
  surface?: EvidenceSourceSurface;
}

/**
 * Build a minimal EnrichedReference from a SimpleCitation for feedback tracking.
 * EnrichedReference extends SimpleCitation but requires `domain: string`.
 */
function toEnrichedReference(source: SimpleCitation): EnrichedReference {
  return {
    ...source,
    domain: source.domain || getDomainFromUrl(source.url || ''),
  };
}

/**
 * Shared colour / transition overrides applied to every action button.
 * Sizing comes from the design-system Button `size` prop (controlled via the component's `size` prop).
 */
const ACTION_BUTTON_CLASS =
  'text-text-tertiary transition-colors duration-200 ease hover:text-text-primary';

export function SourceCardActions({
  source,
  index,
  chatId,
  sessionId,
  messageId,
  className,
  size = 'xs',
  noHoverBg = false,
  feedbackPopoverSide = 'top',
  showSaveAction = false,
  variant = 'v2',
  surface = 'inline_citation_badge',
}: SourceCardActionsProps): JSX.Element {
  const { t } = useTranslations();
  const queryClient = useQueryClient();
  const ports = useEvidenceAnswerPorts();
  const isMobile = useIsMobile();

  const isFileSource = isFileCitation(source.source_type);
  const isPartnerSource = isPartnerCitation(source.source_type);
  const hasUrl = !!source.url;
  const useInlineViewer = shouldOpenSourceInViewer({
    isAuthenticatedUrl: ports.isAuthenticatedUrl,
    url: source.url,
    embed: source.embed,
    isMobile,
    useViewerForDesktopUrls: false,
  });

  const isCpdQueryEnabled = ports.isCpdQueryEnabled;
  const { data: savedSources } = useCPDSavedSources(isCpdQueryEnabled);
  const serverMatch = useMemo(
    () => savedSources?.find((s) => s.url === source.url),
    [savedSources, source.url]
  );

  // Local state for optimistic updates between click and cache refetch.
  // null = "no user interaction yet, derive from cache".
  const [optimistic, setOptimistic] = useState<{
    isSaved: boolean;
    savedId: string | null;
  } | null>(null);

  const isSaved = optimistic?.isSaved ?? !!serverMatch;
  const savedId = optimistic?.savedId ?? serverMatch?.id ?? null;

  const invalidateCPDQueries = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [CPD_SOURCES_INFINITE_KEY] });
    void queryClient.invalidateQueries({ queryKey: [CPD_SAVED_SOURCES_KEY] });
    void queryClient.invalidateQueries({ queryKey: [CPD_HOMEPAGE_KEY] });
  }, [queryClient]);

  const handleSaveToggle = useCallback(() => {
    if (isSaved && savedId) {
      setOptimistic({ isSaved: false, savedId: null });
      void unsaveSourceById(savedId).then((success) => {
        if (success) {
          invalidateCPDQueries();
          setOptimistic(null);
        } else {
          setOptimistic({ isSaved: true, savedId });
        }
      });
    } else if (source.url) {
      setOptimistic({ isSaved: true, savedId: null });
      void saveSourceByUrl(source.url, source.title).then((sourceId) => {
        if (sourceId) {
          setOptimistic({ isSaved: true, savedId: sourceId });
          invalidateCPDQueries();
          toast({
            tone: 'success',
            title: t('chat.readLaterToastTitle'),
            action: (
              <Button
                variant="outline"
                size="xxs"
                className="shrink-0 text-current"
                onClick={() => {
                  ports.onNavigate('/evidence/cpd');
                }}
              >
                {t('chat.viewCpd')}
              </Button>
            ),
          });
        } else {
          setOptimistic(null);
        }
      });
    }
  }, [isSaved, savedId, source.url, source.title, invalidateCPDQueries, ports, t]);

  // Build enriched reference for feedback hook
  const enrichedRef = useMemo(() => toEnrichedReference(source), [source]);

  const {
    selectedFeedback,
    isModalOpen,
    feedbackComment,
    pendingFeedback,
    isSubmitDisabled,
    isSubmitting,
    handleFeedbackClick,
    handleSubmit,
    handleCancel,
    setFeedbackComment,
  } = useReferenceFeedback({
    reference: enrichedRef,
    index,
    chatId,
    sessionId,
    messageId,
  });

  const handleView = useCallback(() => {
    if (!source.url) return;
    // EVI-1486: this is the "eye" / Read-full-source button. The wrapping
    // `<div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}`
    // below intentionally swallows the bubble so the parent
    // `SourceCardV3Popover.onSourceSelect` analytics never fires from here.
    // Emit the unified evidence_source_clicked + button_clicked pair
    // directly so this surface is observable. Track BEFORE `window.open`
    // because `_blank` can preempt the executing context (Electron).
    const domain = source.domain || getDomainFromUrl(source.url);
    ports.trackSourceClick({
      source_url: source.url,
      source_domain: domain,
      source_title: source.title || undefined,
      source_type: source.source_type,
      // EVI-1486 round 7 + EVI-1592: `collections` array (undefined for non-library sources).
      collections: source.collections?.length ? source.collections : undefined,
      surface,
      interaction: useInlineViewer ? 'open_in_viewer' : 'open_in_new_tab',
      chat_id: chatId,
      message_id: messageId,
      session_id: sessionId,
    });
    ports.trackAnswerEvent({
      type: 'source_selected',
      source_url: source.url,
      source_domain: domain,
      source_title: source.title || undefined,
    });
    if (useInlineViewer) {
      ports.openInViewer(buildViewerSource(source, getDomainFaviconUrl));
    } else {
      ports.openInNewTab(source.url);
    }
  }, [
    // EVI-1486 round 7 + EVI-1592: `collections` participates in the emitted
    // analytics payload and must appear in the dep array.
    source,
    surface,
    chatId,
    messageId,
    sessionId,
    useInlineViewer,
    ports,
  ]);

  const getModalTitle = () => {
    if (pendingFeedback === 'positive') {
      return t('feedback.referenceFeedback.positive.title');
    } else if (pendingFeedback === 'negative') {
      return t('feedback.referenceFeedback.negative.title');
    }
    return '';
  };

  const getPlaceholderText = () => {
    if (pendingFeedback === 'positive') {
      return t('feedback.referenceFeedback.positive.placeholder');
    } else if (pendingFeedback === 'negative') {
      return t('feedback.referenceFeedback.negative.placeholder');
    }
    return '';
  };

  // When noHoverBg is set, strip the ghost variant's hover/active background fill.
  // Only the icon colour change (already in ACTION_BUTTON_CLASS) conveys hover state.
  const actionBtnClass = cn(
    ACTION_BUTTON_CLASS,
    noHoverBg && 'hover:bg-transparent active:bg-transparent'
  );

  const isV3Partner = variant === 'v3' && isPartnerSource;

  const partnerCtaLabel =
    isPartnerSource && source.partner_type?.includes('healthpathway')
      ? t('chat.readFullPathway')
      : t('chat.readFullSource');

  const partnerCta =
    isV3Partner && hasUrl ? (
      <Button variant="outline" size="xs" className="gap-1" onClick={handleView}>
        {partnerCtaLabel}
        {useInlineViewer ? <Eye /> : <ExternalLink />}
      </Button>
    ) : null;

  const viewSaveButtons = (
    <div className="flex items-center gap-0.5">
      {!isV3Partner && hasUrl && !isFileSource && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size={size}
              iconOnly
              className={actionBtnClass}
              onClick={handleView}
            >
              <Eye />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            {t('chat.viewSource')}
          </TooltipContent>
        </Tooltip>
      )}

      {/* Hide Read Later when source has no URL — BE save flow is URL-keyed, so partners like EMGuidance/Vidal/Elsevier would otherwise silently no-op. */}
      {showSaveAction && hasUrl && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size={size}
              iconOnly
              className={
                isSaved
                  ? 'ease text-text-link transition-colors duration-200 hover:text-text-primary'
                  : actionBtnClass
              }
              onClick={handleSaveToggle}
            >
              {isSaved ? <BookmarkCheck /> : <Bookmark />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs">
            {isSaved ? t('chat.savedToCpdReadingList') : t('chat.readLater')}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );

  const thumbsButtons = !isFileSource ? (
    <div className={cn('flex items-center gap-0.5', variant === 'v2' && 'ml-auto')}>
      <FeedbackPopover
        feedbackType="positive"
        icon={ThumbsUp}
        filledIcon={ThumbsUpFilled}
        isSelected={selectedFeedback === 'positive'}
        isOpen={isModalOpen && pendingFeedback === 'positive'}
        isSubmitting={isSubmitting}
        disabled={false}
        hasConversation={true}
        feedbackComment={feedbackComment}
        isSubmitDisabled={isSubmitDisabled}
        buttonClassName={actionBtnClass}
        buttonSize={size}
        autoSizeSvg
        alignOffset={-25}
        popoverSide={feedbackPopoverSide}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
        onFeedbackClick={handleFeedbackClick}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        onCommentChange={setFeedbackComment}
        getModalTitle={getModalTitle}
        getPlaceholderText={getPlaceholderText}
      />

      <FeedbackPopover
        feedbackType="negative"
        icon={ThumbsDown}
        filledIcon={ThumbsDownFilled}
        isSelected={selectedFeedback === 'negative'}
        isOpen={isModalOpen && pendingFeedback === 'negative'}
        isSubmitting={isSubmitting}
        disabled={false}
        hasConversation={true}
        feedbackComment={feedbackComment}
        isSubmitDisabled={isSubmitDisabled}
        buttonClassName={actionBtnClass}
        buttonSize={size}
        autoSizeSvg
        alignOffset={-45}
        popoverSide={feedbackPopoverSide}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
        onFeedbackClick={handleFeedbackClick}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        onCommentChange={setFeedbackComment}
        getModalTitle={getModalTitle}
        getPlaceholderText={getPlaceholderText}
      />
    </div>
  ) : null;

  return (
    <TooltipProvider delayDuration={300}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- action row lives inside an <a> tag; preventDefault stops link navigation when clicking action buttons */}
      <div
        className={cn('flex items-center gap-0.5', isV3Partner ? 'w-full' : className)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {partnerCta}
        <div className={cn('flex items-center gap-0.5', isV3Partner && 'ml-auto')}>
          {viewSaveButtons}
          {thumbsButtons}
        </div>
      </div>
    </TooltipProvider>
  );
}
